import React from 'react';
import { PlusCircleIcon, AlertTriangleIcon, ShieldCheckIcon, CheckSquareIcon } from 'lucide-react';
import { ScreenProps } from '../../types/ptp';
import { PrimaryButton } from '../ui/PrimaryButton';
import { SecondaryButton } from '../ui/SecondaryButton';
export function Screen15_NewInfo({
  data,
  updateData,
  onNext
}: ScreenProps) {
  const handleApplyUpdates = () => {
    const updatedSteps = data.steps.map((step, i) => {
      if (i === 4) {
        return {
          ...step,
          hazards: [...step.hazards, {
            hazard: 'Wind affecting load stability',
            controls: ['Stop lift if wind increases or load becomes unstable', 'Confirm radio communication between crane operator and signal person before lift'],
            riskLevel: 'HIGH' as const,
            isNew: true
          }]
        };
      }
      if (i === 5) {
        return {
          ...step,
          hazards: step.hazards.map((h, j) => {
            if (j === 1) {
              return {
                ...h,
                controls: [...h.controls, 'Verify all workers below the lift path are cleared before each lift']
              };
            }
            return h;
          })
        };
      }
      return step;
    });
    updateData({
      steps: updatedSteps,
      selectedHazards: [...data.selectedHazards, 'Wind affecting load stability'],
      engineeringControls: [...data.engineeringControls, 'Stop lift if wind increases or load becomes unstable'],
      adminControls: [...data.adminControls, 'Confirm radio communication between crane operator and signal person before lift'],
      safetyChecks: [...data.safetyChecks, 'Verify all workers below the lift path are cleared before each lift'],
      meetingScore: 72
    });
    onNext();
  };
  return <div className="flex flex-col h-full" data-id="element-621">
      <div className="mb-6" data-id="element-622">
        <h2 className="text-2xl font-bold text-white mb-2" data-id="element-623">
          New Information Detected
        </h2>
        <p className="text-slate-400" data-id="element-624">
          AI found information from the meeting that should be added to the PTP.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto pb-6 -mx-2 px-2 space-y-4" data-id="element-625">
        <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-2" data-id="element-626">
          Detected Additions
        </h3>

        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 border-l-4 border-l-red-500" data-id="element-627">
          <div className="flex items-center gap-2 mb-1" data-id="element-628">
            <AlertTriangleIcon className="w-4 h-4 text-red-500" data-id="element-629" />
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-630">
              Hazard → Step 5: Lift beam
            </span>
          </div>
          <p className="text-white font-medium mb-2" data-id="element-631">
            Wind affecting load stability
          </p>
          <div className="bg-slate-950 rounded p-3" data-id="element-632">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1" data-id="element-633">
              Reason
            </p>
            <p className="text-slate-300 text-sm italic" data-id="element-634">
              The foreman said the wind picks up near midday and the load may
              move during lifting.
            </p>
          </div>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 border-l-4 border-l-green-500" data-id="element-635">
          <div className="flex items-center gap-2 mb-1" data-id="element-636">
            <ShieldCheckIcon className="w-4 h-4 text-green-500" data-id="element-637" />
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-638">
              Control → Step 5: Lift beam
            </span>
          </div>
          <p className="text-white font-medium mb-2" data-id="element-639">
            Stop lift if wind increases or load becomes unstable
          </p>
          <div className="bg-slate-950 rounded p-3" data-id="element-640">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1" data-id="element-641">
              Reason
            </p>
            <p className="text-slate-300 text-sm italic" data-id="element-642">
              The crane operator stated the lift must stop immediately if wind
              conditions change or the beam starts rotating.
            </p>
          </div>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 border-l-4 border-l-green-500" data-id="element-643">
          <div className="flex items-center gap-2 mb-1" data-id="element-644">
            <ShieldCheckIcon className="w-4 h-4 text-green-500" data-id="element-645" />
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-646">
              Control → Step 5: Lift beam
            </span>
          </div>
          <p className="text-white font-medium mb-2" data-id="element-647">
            Confirm radio communication between crane operator and signal person
            before lift
          </p>
          <div className="bg-slate-950 rounded p-3" data-id="element-648">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1" data-id="element-649">
              Reason
            </p>
            <p className="text-slate-300 text-sm italic" data-id="element-650">
              The meeting included discussion about hand signals and radio
              backup.
            </p>
          </div>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 border-l-4 border-l-blue-500" data-id="element-651">
          <div className="flex items-center gap-2 mb-1" data-id="element-652">
            <CheckSquareIcon className="w-4 h-4 text-blue-500" data-id="element-653" />
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-654">
              Control → Step 6: Guide beam
            </span>
          </div>
          <p className="text-white font-medium mb-2" data-id="element-655">
            Verify all workers below the lift path are cleared before each lift
          </p>
          <div className="bg-slate-950 rounded p-3" data-id="element-656">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1" data-id="element-657">
              Reason
            </p>
            <p className="text-slate-300 text-sm italic" data-id="element-658">
              The foreman reminded the crew to keep the area under the suspended
              load clear.
            </p>
          </div>
        </div>

        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mt-6 flex items-center justify-center gap-2" data-id="element-659">
          <PlusCircleIcon className="w-5 h-5 text-yellow-500" data-id="element-660" />
          <span className="text-yellow-400 font-bold" data-id="element-661">
            4 updates will be added to the PTP
          </span>
        </div>
      </div>

      <div className="pt-4 space-y-3 bg-slate-950" data-id="element-662">
        <PrimaryButton onClick={handleApplyUpdates} data-id="element-663">
          Apply Updates to PTP
        </PrimaryButton>
        <SecondaryButton onClick={() => {}} data-id="element-664">Dismiss</SecondaryButton>
      </div>
    </div>;
}